import React from "react";
import { useReducedMotion } from "./motion";

/* PostcardSurface — MASTER_SPEC Draft80 LUX-10, §10.10, BRAND-01.
 *
 * The one object in this product that earns dimensional treatment. CSS 3D and
 * DOM only: no WebGL, so it works in the ordering flow, on a mid-range phone,
 * and with a screen reader.
 *
 * Three rules the component enforces rather than assumes:
 *
 *   Both sides are always readable. The flip is decoration; the back's text is
 *   in the DOM whichever face is showing, and under `prefers-reduced-motion`
 *   the sides swap instantly with no rotation at all. A customer must never
 *   have to wait for, or complete, an animation to read their own message.
 *
 *   The mail canvas has an edge. Everything CareCard's interface adds —
 *   labels, the flip control, the safe-area guides — sits outside it, so
 *   nobody mistakes interface chrome for something that gets printed.
 *
 *   The provider's zones are shown as *reserved*, not as content. PostGrid
 *   stamps the address block, the indicia and the barcode; CareCard draws a
 *   photo and a message. Drawing our own address block would fight the real
 *   one, which is the failure §10.10.2 exists to prevent.
 */

const FRONT = "front";
const BACK = "back";

const DEFAULT_CARD_LABELS = {
  front: "Front",
  back: "Back",
  toggleLabel: "Postcard side",
  note: "A preview of what will be printed. The mail partner adds the address and postage in the reserved areas.",
  photoAria: "A sample photograph standing in for the photograph you would send",
  photoAlt: "The photo on the front of your postcard",
  photoEmpty: "Your photo",
  photoEmptySub: "Fills the whole front of the card",
  messageEmpty: "Your message goes here.",
  zoneAddress: "Address & postage",
  zoneAddressSub: "Printed by the mail partner",
  zoneBarcode: "Postal barcode",
};

export function PostcardSurface({
  photoUrl,
  // An already-rendered front, for the marketing surfaces. The homepage uses
  // the repository's own AI-generated sample photograph of a fictional family
  // rather than a stock photograph of real people, which would be a licensing
  // problem and a strange thing to put on this particular page.
  photoNode = null,
  message = "",
  recipientSummary = null,
  side: controlledSide,
  onSideChange,
  showSafeArea = false,
  showProviderZones = true,
  proofNote = true,
  size = "md",
  id = "postcard",
  // UI-chrome strings; English by default, callers pass localized copy.
  labels = null,
}) {
  const L = { ...DEFAULT_CARD_LABELS, ...(labels || {}) };
  const reduced = useReducedMotion();
  const [uncontrolled, setUncontrolled] = React.useState(FRONT);
  const side = controlledSide || uncontrolled;
  const setSide = onSideChange || setUncontrolled;

  const flipped = side === BACK;
  const frontId = `${id}-front`;
  const backId = `${id}-back`;

  return (
    <div className={`cc-card-stage cc-card-${size}`}>
      <div
        className="cc-card"
        data-flipped={flipped ? "true" : "false"}
        data-reduced={reduced ? "true" : "false"}
      >
        {/* Front: full-bleed customer photo. Nothing else is printed here. */}
        <figure
          className="cc-card-face cc-card-front"
          id={frontId}
          aria-hidden={flipped ? "true" : undefined}
        >
          {photoNode ? (
            <div className="cc-card-photo" role="img"
                 aria-label={L.photoAria}>
              {photoNode}
            </div>
          ) : photoUrl ? (
            <img src={photoUrl} alt={L.photoAlt} />
          ) : (
            <div className="cc-card-empty">
              <span>{L.photoEmpty}</span>
              <span className="cc-card-empty-sub">{L.photoEmptySub}</span>
            </div>
          )}
          {showSafeArea && <span className="cc-card-safe" aria-hidden="true" />}
        </figure>

        {/* Back: the message, in the region CareCard is allowed to print. */}
        <div
          className="cc-card-face cc-card-back"
          id={backId}
          aria-hidden={flipped ? undefined : "true"}
        >
          <div className="cc-card-message">
            {message
              ? <p>{message}</p>
              : <p className="cc-card-message-empty">{L.messageEmpty}</p>}
          </div>
          {showProviderZones && (
            <>
              <div className="cc-card-zone cc-card-zone-address" aria-hidden="true">
                <span>{L.zoneAddress}</span>
                <span className="cc-card-zone-sub">{L.zoneAddressSub}</span>
              </div>
              <div className="cc-card-zone cc-card-zone-barcode" aria-hidden="true">
                <span>{L.zoneBarcode}</span>
              </div>
            </>
          )}
          {showSafeArea && <span className="cc-card-safe" aria-hidden="true" />}
        </div>
      </div>

      {/* Controls sit outside the card edge: they are interface, not artwork. */}
      <div className="cc-card-controls">
        <div className="cc-card-toggle" role="group" aria-label={L.toggleLabel}>
          <button
            type="button"
            className="cc-card-toggle-btn"
            aria-pressed={!flipped}
            aria-controls={frontId}
            onClick={() => setSide(FRONT)}
          >
            {L.front}
          </button>
          <button
            type="button"
            className="cc-card-toggle-btn"
            aria-pressed={flipped}
            aria-controls={backId}
            onClick={() => setSide(BACK)}
          >
            {L.back}
          </button>
        </div>
        {proofNote && (
          <p className="cc-card-note">
            {L.note}
          </p>
        )}
      </div>

      {/* The back's content is in the DOM on both sides, so a screen reader
          never depends on which face is visually forward. */}
      {recipientSummary && <div className="cc-card-recipient">{recipientSummary}</div>}
    </div>
  );
}

export default PostcardSurface;
